import type { DateTimeType, DateType, DateTimeUnixType } from '@/js/libs/api-helpers';
import * as Permissions from '@/js/libs/generated/permissions/types';
import * as Types from './types';

export type IdCompanyDto = { _id: 'ws-companies:CompanyDto'; toString: () => string };
export type IdAccountDto = { _id: 'ws-crm:AccountDto'; toString: () => string };
export type IdWebsiteDto = { _id: 'ws-digital:WebsiteDto'; toString: () => string };
export type IdUserGroupDto = { _id: 'ws-permissions:UserGroupDto'; toString: () => string };
export type IdUserDto = { _id: 'ws-companies:UserDto'; toString: () => string };
export type CompanyPermissionsSummaryDto = {
  administratingCompanyIds: string[];
  companyId: string;
  entityAccessDetails: Types.EntityAccessDetailsDto[];
  managedBySelfbookingCompanyId?: string;
  permissionSharingDetails: Types.PermissionSharingDetailsDto[];
  permittedAccountIds: string[];
  permittedWebsiteIds: string[];
  selfbookingManagingCompanyIds: string[];
};
export type PermissionSharingDetailsDto = {
  accessAllowedByCompanies: string[];
  entityTypes: string[];
  permissionType: string;
};
export type EntityAccessDetailsDto = { accessLevel: string; debugInfo?: Record<string, string>; entityType: string };
export type CompanyPermissionsAssignmentForm = { permissionStatuses: Types.PermissionStatusForm[] };
export type PermissionStatusForm = { permissionType: string; status: boolean };
export type CompanyPermissionsUpdateForm = { customizations?: Types.CustomizedForm[] };
export type CustomizedForm = { permissionType: string; sharedWithCompanyIds: IdCompanyDto[]; status: boolean };
export type CompanyPermissionsDto = {
  customizations: Types.CustomizedDto[];
  id: string;
  permittedAccountIds: IdAccountDto[];
  permittedWebsiteIds: IdWebsiteDto[];
};
export type CustomizedDto = { enabled: boolean; permissionType: string; sharedWithCompanyIds: IdCompanyDto[] };
export type UserGroupForm = { active?: boolean; companyId: IdCompanyDto; name: string; permissionTypes: string[] };
export type UserGroupDto = {
  active: boolean;
  id: IdUserGroupDto;
  name: string;
  permissionTypes: string[];
  restrictions: Types.Restriction[];
};
export type PaginatedUserGroup = { items: Types.UserGroupDto[]; total: number };
export type CompactUserDto = {
  acceptTokenAfter?: DateTimeUnixType;
  administratingCompanyIds: IdCompanyDto[];
  companyId: IdCompanyDto;
  companyName: string;
  companyUuid: string;
  entityAccessDetails: Types.EntityAccessDetailsDto[];
  managedBySelfbookingCompanyId?: string;
  permissionSharingDetails: Types.PermissionSharingDetailsDto[];
  permittedAccountIds: string[];
  permittedWebsiteIds: string[];
  selfbookingManagingCompanyIds: string[];
  userEmail: string;
  userEnabled: boolean;
  userId: string;
  userRole: string;
};
export type PaginatedAccounts = { items: Types.AccountDto[]; total: number };
export type AccountDto = {
  company: CompanyDto;
  displayName: string;
  displayNameWithCompany: string;
  hasLinkedCompany: boolean;
  id: IdAccountDto;
  roles: Types.Role[];
  shortName: string;
};
export type CompanyDto = {
  companyUUID?: string;
  countryCode?: string;
  id: IdCompanyDto;
  name: string;
  temporaryAccessRequired?: boolean;
  website?: string;
};
export type UserDto = {
  administratingCompanyIds: string[];
  company: CompanyDto;
  email: string;
  firstName?: string;
  id: IdUserDto;
  language?: string;
  lastName?: string;
  name: string;
  permissions: Permissions.PermissionType[];
  permittedAccountIds: string[];
  permittedWebsiteIds: string[];
  preferredLanguages?: string[];
  userGroupIds: string[];
  userRole?: UserRole;
};
export enum UserRole {
  ADMIN = 'ADMIN',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  USER = 'USER',
}
export enum AccessLevel {
  CREATE = 'CREATE',
  READ_ONLY = 'READ_ONLY',
}
export enum Role {
  ADVERTISER = 'ADVERTISER',
  AGENCY = 'AGENCY',
  BROKER = 'BROKER',
  OTHER = 'OTHER',
  PUBLISHER = 'PUBLISHER',
}
export enum Restriction {
  POSTNETZ_BRANCH_USER_RESTRICTION = 'POSTNETZ_BRANCH_USER_RESTRICTION',
  READ_ONLY_POSTNETZ_BRANCH_USER_RESTRICTION = 'READ_ONLY_POSTNETZ_BRANCH_USER_RESTRICTION',
}
export type CompanyNameDto = {
  companyUUID: string;
  countryCode: string;
  id: IdCompanyDto;
  name: string;
  temporaryAccessRequired: boolean;
};
export type PaginatedCompaniesNames = { items: Types.CompanyNameDto[]; total: number };
